import React, { useEffect, useState, useCallback } from 'react'
import { Button, Drawer, TextInput, Skeleton } from '@mantine/core'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectCurrentProject,
  selectProjectSidebarOpen,
  setModal,
  setProjectSidebarOpen,
} from '../../redux/application-slice'
import {
  useGetProjectsQuery,
  useUpdateProjectOrderMutation,
} from '../../redux/api-slice'
import ProjectButton from './project-button'
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd'
import { Project } from '../../shared/interfaces/project/project.interface'
import { MODAL_TYPES } from '../modals/modal-controller'
import ProjectSettings from '../modals/project-settings'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { Paper } from '@mantine/core'

interface OrderedProject extends Project {
  sort_index: number
}

export default function ProjectSelectorDrawer() {
  const projectSidebarOpen = useSelector(selectProjectSidebarOpen)
  const { currentData: projects, isLoading } = useGetProjectsQuery()
  const [orderedProjects, setOrderedProjects] = useState<OrderedProject[]>([])
  const [updateProjectOrder] = useUpdateProjectOrderMutation()
  const [search, setSearch] = useState('')
  const dispatch = useDispatch()
  const currentProject = useSelector(selectCurrentProject)
  const [openProjectSettings, setOpenProjectSettings] =
    useState<Project | null>(null)

  const close = useCallback(() => {
    if (!currentProject) return
    dispatch(setProjectSidebarOpen(false))
    setOpenProjectSettings(null)
  }, [currentProject, dispatch])

  useEffect(() => {
    setOrderedProjects(
      projects?.map((project, index) => ({ ...project, sort_index: index })) ??
        []
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects])

  const draggableProjects = React.useMemo(() => {
    if (isLoading) {
      return Array(3)
        .fill(0)
        .map((_, i) => <Skeleton key={i} height={30} mb="sm" radius="sm" />)
    }

    const filteredProjects = orderedProjects?.filter((project) =>
      project.title?.toLowerCase().includes(search.toLowerCase())
    )
    if (!orderedProjects?.length) {
      return (
        <div className="mt-4 text-center text-gray-500">
          No projects yet. Create your first project to get started!
        </div>
      )
    }
    if (!filteredProjects?.length) {
      return (
        <div className="mt-4 text-center text-gray-500">
          No projects match your search
        </div>
      )
    }
    return filteredProjects.map((orderedProject, index) => (
      <ProjectButton
        key={orderedProject.id}
        project={orderedProject}
        sortIndex={index}
        isDraggingDisabled={!!search}
        dragDisabledMessage="Reordering is disabled while searching"
        onSettingsChange={() => handleProjectSettings(orderedProject)}
      />
    ))
  }, [orderedProjects, search, isLoading])

  const handleProjectSettings = useCallback((project: Project) => {
    setOpenProjectSettings(project)
  }, [])

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (search) return // Prevent reordering while searching

      const newOrder = [...orderedProjects]
      const [removed] = newOrder.splice(result.source.index, 1)
      newOrder.splice(result.destination?.index || 0, 0, removed)

      setOrderedProjects(newOrder)
      updateProjectOrder(
        Object.fromEntries(
          newOrder.map((project, index) => [
            project.id ?? 0,
            newOrder.length - 1 - index,
          ])
        )
      )
    },
    [search, orderedProjects, updateProjectOrder]
  )

  const createNewProject = useCallback(() => {
    dispatch(setModal({ modal: MODAL_TYPES.CREATE_PROJECT }))
  }, [dispatch])

  return (
    <Drawer
      opened={projectSidebarOpen}
      onClose={close}
      size="lg"
      title="Projects"
      closeOnClickOutside={!!currentProject}
      closeOnEscape={!!currentProject}
      withCloseButton={!!currentProject}
      className="h-full flex-1 overflow-hidden"
      styles={{
        inner: {
          height: '100vh',
        },
        body: {
          height: 'calc(100vh - 60px)',
        },
        header: {
          height: '60px',
        },
      }}
    >
      {openProjectSettings && (
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex flex-row justify-between bg-white">
            <div className="flex items-center [grid-area:1_/_1_/_1_/_2]">
              <Paper
                radius="md"
                p="6px"
                bg="#ffffff00"
                className="group ml-4 cursor-pointer border-2  border-[#dfe2e8] !bg-[#fafafa] transition-colors hover:border-blue-500 hover:!bg-[#fff]"
                onClick={() => setOpenProjectSettings(null)}
              >
                <div className="mr-2 flex items-center justify-center text-[16px] font-medium !text-black group-hover:!text-blue-900">
                  <ChevronLeftIcon className="mr-2 h-6 w-6 text-black group-hover:text-blue-900" />
                  Go Back to Projects
                </div>
              </Paper>
            </div>
            <h3 className="flex items-center text-lg font-semibold leading-6 text-gray-900">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <Cog6ToothIcon
                  className="h-6 w-6 text-blue-600"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-2">Project Settings</div>
            </h3>
          </div>
          <div className="flex-1 overflow-hidden">
            <ProjectSettings />
          </div>
        </div>
      )}
      {!openProjectSettings && (
        <>
          <div
            className="mb-4 mt-1 flex gap-2"
            data-testid="project-selector-drawer-header"
          >
            <TextInput
              placeholder="Search projects"
              leftSection={
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
              }
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              radius="sm"
              className="flex-1"
            />
            <Button
              leftSection={<PlusIcon className="h-4 w-4 text-gray-500" />}
              variant="default"
              onClick={createNewProject}
            >
              Create a New Project
            </Button>
          </div>
          <div className="flex flex-col">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="dnd-list" direction="vertical">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {draggableProjects}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </>
      )}
    </Drawer>
  )
}
